import React, { useEffect, useState } from 'react';
import BudgetCarousel from '@/components/molecules/BudgetCarousel';
import { Card, CardContent } from '@/components/ui/card';
import budgetsIcon from '@/assets/icons/budgets.svg';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { formatDate, getCurrencyInfo } from '@/utils/helpers';
import arrowRightIcon from '@/assets/icons/arrow-right.svg';
import { budgetService } from '@/services/budget.service';
import { useDispatch } from 'react-redux';
import { setBreadcrumbs } from '@/store/slices/breadcrumb';
import { PAGE } from '@/routes';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import ColorPicker from '@/components/molecules/ColorPicker';
import { ChevronDown } from 'lucide-react';
import GradientImg from '@/assets/images/gradient-color.png';
import isMobile from 'is-mobile';

interface Budget {
  id: string;
  title: string;
  company: string;
  btw: string;
  remainingBtw: string;
  color: string;
  description?: string;
  startingThreshold?: string;
  endingThreshold?: string;
  startDate?: string;
  endDate?: string;
  name?: string;
  type?: string;
  icon_id?: string;
  color_hex?: string;
  currency?: string;
  bmevAlertThreshold?: number;
  btrtAlertThreshold?: number;
  isAutoRenewal?: boolean;
}

interface BudgetSectionProps {
  // budgets: Budget[];
}

export const BudgetSection: React.FC<BudgetSectionProps> = () => {
  const [budgets, setBudgets] = useState<any[]>([]);
  const [selectedBudget, setSelectedBudget] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [isExpanded, setIsExpanded] = useState(false);
  const [selectedColor, setSelectedColor] = useState<string>('');
  const [open, setOpen] = useState(false);
  const [openMobile, setOpenMobile] = useState(false);

  const navigate = useNavigate();

  const dispatch = useDispatch();

  const isNoBudgets = budgets.length === 0 && !loading;

  const filteredBudgets = budgets.filter((budget) => budget.color_hex === selectedColor || selectedColor === '');

  useEffect(() => {
    // Sets breadcrumbs when component mounts
    dispatch(
      setBreadcrumbs([
        {
          title: 'Budgets',
          path: PAGE.BUDGETS.path
        }
      ])
    );
  }, []);

  const fetchBudgets = async () => {
    try {
      const { data: budgetsData } = await budgetService.getBudgets();

      setSelectedBudget(budgetsData[0]);
      setBudgets(budgetsData);
    } catch (error) {
      console.error('Error fetching budgets:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBudgets();
  }, []);

  const handleBudgetSelect = (budget: any) => {
    setSelectedBudget(budget);
  };

  const handleDetailClick = () => {
    navigate(`/budgets/${selectedBudget?._id}`);
  };

  if (loading) {
    return <div className="flex items-center justify-center -mt-8 h-full">Loading...</div>;
  }

  return (
    <div className="flex-1 flex flex-col gap-6 md:gap-8 p-4 md:p-0">
      <div className="flex-1 flex flex-col md:h-auto md:bg-white md:p-5 overflow-hidden rounded-[16px] md:border">
        <header className="hidden md:flex items-center gap-x-7 gap-y-0 justify-between mb-[1rem] flex-wrap">
          <h2 className="text-2xl font-semibold flex items-center gap-2">
            {' '}
            <img src={budgetsIcon} alt="" /> My Budget
          </h2>

          <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger className="font-medium flex items-center gap-2">
              <span className="block w-3 h-3 rounded-full" style={{ backgroundColor: selectedColor }}></span>
              <span className="text-sm font-medium">Filter by color</span>
              <ChevronDown
                className={`ml-2 w-5 h-5 ${open ? 'rotate-180' : ''} ${selectedColor !== '' ? 'text-primary' : ''}`}
              />
            </PopoverTrigger>
            <PopoverContent className="py-3 px-4 w-fit border border-[#E9ECEF] rounded-[12px] shadow-xl">
              <ColorPicker
                value={selectedColor}
                onChange={(color) => {
                  if (color === selectedColor) {
                    setSelectedColor('');
                  } else {
                    setSelectedColor(color);
                  }
                  setOpen(false);
                }}
                className="grid grid-cols-4 gap-3"
              />
            </PopoverContent>
          </Popover>

          <Button
            className="hidden md:block rounded-[24px] text-base font-semibold px-6 pl-0 py-0 h-[38px] text-[16px] w-full md:w-fit text-primary"
            variant={'text'}
            onClick={() => {
              navigate('/budgets/add');
            }}
            // disabled={isSubmitting}
            type="button"
          >
            + Create a Budget
          </Button>
        </header>
        <header className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <h2 className="text-base font-bold md:hidden">My Budgets</h2>
            <div className="md:hidden">
              <Popover open={openMobile} onOpenChange={setOpenMobile}>
                <PopoverTrigger className="font-medium flex items-center gap-2">
                  <img src={GradientImg} alt="" />
                  <ChevronDown
                    className={`w-5 h-5 ${open ? 'rotate-180' : ''} ${selectedColor !== '' ? 'text-primary' : ''}`}
                  />
                </PopoverTrigger>
                <PopoverContent className="py-3 px-4 w-fit border border-[#E9ECEF] rounded-[12px] shadow-xl">
                  <ColorPicker
                    value={selectedColor}
                    onChange={(color) => {
                      if (color === selectedColor) {
                        setSelectedColor('');
                      } else {
                        setSelectedColor(color);
                      }
                      setOpen(false);
                    }}
                    className="grid grid-cols-4 gap-3"
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>
          {!isNoBudgets && (
            <button onClick={() => navigate('/budgets/add')} className="text-sm font-semibold text-primary md:hidden">
              + Create a Budget
            </button>
          )}
        </header>
        <BudgetCarousel
          budgets={filteredBudgets}
          onBudgetSelect={handleBudgetSelect}
          filterApplied={selectedColor !== ''}
        />
      </div>

      {selectedBudget && (
        <Card className="w-full mx-auto rounded-[16px] border shadow-none">
          <CardContent className="p-4 md:p-5">
            <h2 className="text-sm md:text-2xl font-semibold mb-4 border-b pb-[0.8rem] md:pb-[1.1rem]">
              Budget Information
            </h2>
            <div className="grid grid-cols-2 gap-5">
              <div className="col-span-full">
                <p className="text-xs md:text-sm text-lightPurple-300">Description</p>
                <p className={`font-medium text-sm md:text-base text-gray ${isExpanded ? '' : 'line-clamp-2'}`}>
                  {selectedBudget.description}
                </p>
                {!isExpanded && selectedBudget.description && selectedBudget.description.length > 40 && (
                  <Button variant={'text'} className="h-fit p-0 m-0 text-sm" onClick={() => setIsExpanded(true)}>
                    See More
                  </Button>
                )}
                {isExpanded && (
                  <Button variant={'text'} className="h-fit p-0 m-0 text-sm" onClick={() => setIsExpanded(false)}>
                    See Less
                  </Button>
                )}
              </div>
              <div className="">
                <p className="text-xs md:text-sm text-lightPurple-300">Alerting threshold of BMEV</p>
                <p className="font-medium text-sm md:text-base text-gray">
                  {selectedBudget?.bmev_alert_threshold || '-'}
                </p>
              </div>
              <div className="">
                <p className="text-xs md:text-sm text-lightPurple-300">Alerting threshold of BTRT</p>
                <p className="font-medium text-sm md:text-base text-gray">
                  {selectedBudget?.btrt_alert_threshold || '-'}
                </p>
              </div>

              <div className="hidden md:block">
                <p className="text-sm text-lightPurple-300">Default Currency Type</p>
                <p className="font-medium text-base text-gray">{getCurrencyInfo(selectedBudget.currency || '')}</p>
              </div>
              <div className="hidden md:block">
                <p className="text-sm text-lightPurple-300">Automatic Renewal</p>
                <p className="font-medium text-base text-gray">{selectedBudget.isAutoRenewal ? 'On' : 'Off'}</p>
              </div>
              <div className="hidden md:block">
                <p className="text-sm text-lightPurple-300">Budget starting date</p>
                <p className="font-medium text-base text-gray">{formatDate(selectedBudget.start_date || '')}</p>
              </div>
              <div className="hidden md:block">
                <p className="text-sm text-lightPurple-300">Budget ending date</p>
                <p className="font-medium text-base text-gray">{formatDate(selectedBudget.end_date || '')}</p>
              </div>
              <div className="col-span-full mt-6">
                <Button className="w-full text-sm rounded-full h-auto" onClick={handleDetailClick}>
                  SEE BUDGET DETAIL
                  <img className="ml-2" src={arrowRightIcon} alt="" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
